import React, {
  Component,
  ErrorInfo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FragmentInstance, LayoutItem, WIDGET_COMPONENT_MAP, } from '@moment/shared-code/widgets.tsx';
import { TripodContext } from '@moment/context/TripodContext.tsx'; // Assuming type is available via alias
import { LiveFragmentRenderer } from './LiveFragmentRenderer';

import './LivePreview.scss';
import { useParams, useSearchParams } from 'react-router';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { generateFetchFeedQuery } from '@/moment/net/feed-fetch-query.ts';
import { feedSelectFn } from '@moment/utils/seeded-shuffle.ts';
import { FragmentLayoutItem } from '@moment/shared-code/fragment-widgets.tsx';
import { EnhancedLightboxItem } from '@moment/net/softbox-types.ts';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator.tsx';
import { generatePublisherFetchQuery } from '@moment/net/publisher-fetch-query.ts';
import { PersonalizedLiveFragmentRenderer } from './PersonalizedLiveFragmentRenderer';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';
import { ObservableWidgetContainer } from '@moment/components/WidgetContainer/ObservableWidgetContainer.tsx';

interface TripodLivePreviewProps {
  pageId?: any;
  pageData?: any; //not in use but exists in payload of tabs
  pageIdFromRoute?: any;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const WIDGET_IMPRESSION_EVENT_LIST = ['horoscopes', 'horoscopesv2wrapper'];

/**
 * Error Boundary wrapper for TripodLivePreview to catch JS errors
 */
class LivePreviewErrorBoundary extends Component<
  {
    children: ReactNode;
    onError: (error: Error, errorInfo: ErrorInfo) => void;
  },
  ErrorBoundaryState
> {
  constructor(props: {
    children: ReactNode;
    onError: (error: Error, errorInfo: ErrorInfo) => void;
  }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Return null or a fallback UI - the error event has already been emitted
      return null;
    }

    return this.props.children;
  }
}

/**
 * Internal component that renders the live preview
 */
const TripodLivePreviewInternal: React.FC<TripodLivePreviewProps> = ({
  pageId: pageIdFromDynamicRoute,
  pageIdFromRoute,
}) => {
  // The context now provides a composite object. We need the `pageData` part.
  const { tripodData, isLoading: isLoadingTripodData } =
    useContext(TripodContext) || {};
  const [searchParams] = useSearchParams();
  const analytics = useAnalytics();
  const { shuffleSeed } = useContext(CommonPageDataContext);
  const context = useContext(CommonPageDataContext);
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [pageLoadedEventEmitted, setPageLoadedEventEmitted] =
    useState<boolean>(false);
  const [pageLoadComplete, setPageLoadComplete] = useState<boolean>(
    typeof document !== 'undefined' && document.readyState === 'complete'
  );
  const routeParams = useParams();

  // Determine the pageId from route params or props.
  const pageId = pageIdFromRoute
    ? routeParams.revision
    : pageIdFromDynamicRoute;

  // Data resolution is now much simpler.
  // 1. If it's a standalone page, use `tripodData.pageData`.
  // 2. If it's a page within a Tab Container, find its data in `tripodData.pageDataMap` using its ID.
  // This logic works for both SSR (data is pre-loaded) and CSR (data is fetched by TripodStateProvider).

  const pageData = useMemo(() => {
    if (pageId && tripodData?.pageDataMap) {
      return tripodData.pageDataMap[pageId];
    }
    return tripodData?.pageData;
  }, [pageId, tripodData?.pageData, tripodData?.pageDataMap]);

  const { revision, fragments = {} } = pageData || {};
  const routeSchedule = routeParams.schedule as string | undefined;

  // --- START: Centralized Feed Fetching & Distribution ---

  // 1. Get all fragment instances from the layout
  const fragmentInstances = useMemo((): FragmentInstance[] => {
    if (!revision?.layoutContent) return [];
    return revision.layoutContent.filter(
      (item: LayoutItem) => item.itemType === 'fragment'
    ) as FragmentInstance[];
  }, [revision?.layoutContent]);

  // 2. Determine the unique schedules and publishers needed for all fragments based on source
  const uniqueSchedulesAndPublishers = useMemo(() => {
    const scheduleSet = new Set<string>();
    const publishers = new Set<string>();
    // Track unique schedule+trending combinations
    const scheduleTrendingCombos = new Map<
      string,
      { schedule: string; trending: boolean }
    >();

    fragmentInstances.forEach((fragment) => {
      if (fragment.props.source === 'schedule' || !fragment.props.source) {
        const schedule = routeSchedule || fragment.props.schedule;
        if (schedule) {
          scheduleSet.add(schedule);
          const trending = fragment.props.trending || false;
          // Create a unique key for this schedule+trending combination
          const comboKey = `${schedule}:${trending}`;
          scheduleTrendingCombos.set(comboKey, { schedule, trending });
        }
      } else if (fragment.props.source === 'publisher') {
        const publisher = fragment.props.publisher;
        if (publisher) {
          publishers.add(publisher);
        }
      } else if (fragment.props.source === 'personalized-feed') {
        console.warn('Personalized feed not implemented yet');
      }
    });
    return {
      schedules: Array.from(scheduleSet),
      publishers: Array.from(publishers),
      scheduleTrendingCombos: Array.from(scheduleTrendingCombos.values()),
    };
  }, [fragmentInstances, routeSchedule]);

  // 3. Fetch all unique feeds in parallel (including separate queries for trending/non-trending)
  const feedQueries = useQueries({
    queries: uniqueSchedulesAndPublishers.scheduleTrendingCombos.map(
      ({ schedule, trending }) => ({
        queryKey: ['feed', schedule, language, trending],
        queryFn: generateFetchFeedQuery(schedule)({ lang: language, trending }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !!schedule,
        select: feedSelectFn(shuffleSeed),
      })
    ),
  });

  const publisherQueries = useQueries({
    queries: uniqueSchedulesAndPublishers.publishers.map((publisher) => ({
      queryKey: ['publisher', publisher, language],
      queryFn: generatePublisherFetchQuery(publisher)({ lang: language }),
      staleTime: 1000 * 60 * 5, // 5 minutes
      enabled: !!publisher,
      select: feedSelectFn(shuffleSeed),
    })),
  });

  // 4. Consolidate all feed items into a single pool with schedule/publisher tracking
  const { allItems, interestsPool, isLoadingFeeds, hasApiError } =
    useMemo(() => {
      const isLoading =
        feedQueries.some((q) => q.isLoading) ||
        publisherQueries.some((q) => q.isLoading);
      const hasError =
        feedQueries.some((q) => q.error) ||
        publisherQueries.some((q) => q.error);

      if (isLoading) {
        return {
          allItems: [],
          interestsPool: {},
          isLoadingFeeds: true,
          hasApiError: false,
        };
      }

      if (hasError) {
        return {
          allItems: [],
          interestsPool: {},
          isLoadingFeeds: false,
          hasApiError: true,
        };
      }

      // Consolidate all items from all feeds and publishers, tracking source and identifier
      const itemsWithSource: Array<{
        item: any;
        source: 'schedule' | 'publisher';
        identifier: string;
        trending?: boolean;
      }> = [];
      const seenUids = new Set<string>();
      const allInterests = new Map<string, any>();

      // Process schedule-based feeds
      feedQueries.forEach((query, index) => {
        const { schedule, trending } =
          uniqueSchedulesAndPublishers.scheduleTrendingCombos[index];
        if (query.data?.news?.items) {
          query.data.news.items.forEach((item: any) => {
            // Only add if we haven't seen this uid before (deduplicate across schedules)
            if (!seenUids.has(item.uid)) {
              seenUids.add(item.uid);
              itemsWithSource.push({
                item,
                source: 'schedule',
                identifier: schedule,
                trending,
              });
            }
          });
        }
        if (query.data?.interests) {
          Object.entries(query.data.interests).forEach(([key, value]) => {
            if (!allInterests.has(key)) {
              allInterests.set(key, value);
            }
          });
        }
      });
      // Process publisher-based feeds
      publisherQueries.forEach((query, index) => {
        const publisher = uniqueSchedulesAndPublishers.publishers[index];
        if (query.data?.news?.items) {
          query.data.news.items.forEach((item: any) => {
            // Only add if we haven't seen this uid before (deduplicate across publishers)
            if (!seenUids.has(item.uid)) {
              seenUids.add(item.uid);
              itemsWithSource.push({
                item,
                source: 'publisher',
                identifier: publisher,
              });
            }
          });
        }
        if (query.data?.interests) {
          Object.entries(query.data.interests).forEach(([key, value]) => {
            if (!allInterests.has(key)) {
              allInterests.set(key, value);
            }
          });
        }
      });

      return {
        allItems: itemsWithSource,
        interestsPool: Object.fromEntries(allInterests),
        isLoadingFeeds: false,
        hasApiError: false,
      };
    }, [feedQueries, publisherQueries, uniqueSchedulesAndPublishers]);

  // --- END: Centralized Feed Fetching & Distribution ---

  const renderedLayout = useMemo(() => {
    if (!revision?.layoutContent) return null;

    // The layoutContent is an array of Widgets or Fragments
    const layout = revision.layoutContent as LayoutItem[];
    // Track cursor per schedule/publisher (since multiple fragments can share a schedule/publisher)
    const scheduleCursors = new Map<string, number>();
    const publisherCursors = new Map<string, number>();

    return layout.map((item) => {
      if (item.itemType === 'fragment') {
        const fragmentData = fragments[item.fragmentId];
        const source = item.props?.source ?? 'schedule';

        if (source === 'personalized-feed') {
          return (
            <PersonalizedLiveFragmentRenderer
              key={item.instanceId}
              fragment={item}
              pageData={pageData}
            />
          );
        }

        const activeRevision =
          fragmentData?.revisions.find(
            (r: any) => r.id === fragmentData.activeRevisionId
          ) || fragmentData?.revisions[0];
        const layoutContent = activeRevision?.layoutContent as
          | FragmentLayoutItem[]
          | undefined;
        const contentItemCount =
          layoutContent?.filter(
            (i) => i.type.startsWith('Story') || i.type.startsWith('Game')
          ).length || 0;

        // Get the schedule for this fragment
        const schedule = routeSchedule || item.props.schedule;

        // Filter allItems to only include items from this fragment's schedule
        const itemsForSchedule = allItems.filter(
          ({ identifier: itemSchedule }) => itemSchedule === schedule
        );

        // Get the current cursor for this schedule
        const cursor = scheduleCursors.get(schedule) || 0;

        // Take items from the filtered list
        let itemsForFragment = itemsForSchedule
          .slice(cursor, cursor + contentItemCount)
          .map(({ item }) => item);

        // Update the cursor for this schedule
        scheduleCursors.set(schedule, cursor + contentItemCount);

        if (source === 'schedule' || !source) {
          // Get the schedule for this fragment
          const schedule = routeSchedule || item.props.schedule;
          const fragmentTrending = item.props.trending || false;

          // Filter allItems to only include items from this fragment's schedule AND trending status
          const itemsForSchedule = allItems.filter(
            ({ source: itemSource, identifier, trending: itemTrending }) =>
              itemSource === 'schedule' &&
              identifier === schedule &&
              itemTrending === fragmentTrending
          );

          // Get the current cursor for this schedule+trending combination
          const cursorKey = `${schedule}:${fragmentTrending}`;
          const cursor = scheduleCursors.get(cursorKey) || 0;

          // Take items from the filtered list
          itemsForFragment = itemsForSchedule
            .slice(cursor, cursor + contentItemCount)
            .map(({ item }) => item);

          // Update the cursor for this schedule+trending combination
          scheduleCursors.set(cursorKey, cursor + contentItemCount);
        } else if (source === 'publisher') {
          // Get the publisher for this fragment
          const publisher = item.props.publisher;
          if (!publisher) return null;

          // Filter allItems to only include items from this fragment's publisher
          // Also filter by ownerUID to ensure items match the publisher
          const itemsForPublisher = allItems.filter(
            ({ source: itemSource, identifier, item: itemData }) =>
              itemSource === 'publisher' &&
              identifier === publisher &&
              itemData.ownerUID === publisher
          );

          // Get the current cursor for this publisher
          const cursor = publisherCursors.get(publisher) || 0;

          // Take items from the filtered list
          itemsForFragment = itemsForPublisher
            .slice(cursor, cursor + contentItemCount)
            .map(({ item }) => item);

          // Update the cursor for this publisher
          publisherCursors.set(publisher, cursor + contentItemCount);
        }
        return (
          <LiveFragmentRenderer
            key={item.instanceId}
            fragment={item}
            pageData={pageData}
            // Pass down the precise data the fragment needs
            isLoading={isLoadingFeeds}
            newsItems={itemsForFragment.map((i) => new EnhancedLightboxItem(i))}
            interests={interestsPool}
            source={source}
            sourceSchedule={item.props?.schedule}
          />
        );
      }

      // Handle standard widgets
      const Component = WIDGET_COMPONENT_MAP[item.type];
      if (!Component) {
        console.warn(
          `TripodLivePreview: Unknown widget type "${item.type}" found in layout.`
        );
        return (
          <div key={item.instanceId}>Unknown widget type: {item.type}</div>
        );
      }

      const trackWidgetEvents = WIDGET_IMPRESSION_EVENT_LIST.find(
        (i) => i === item.type.toLowerCase()
      );

      const handleWidgetClick = (target: string) => {
        if (trackWidgetEvents) {
          analytics.emitWidgetClickedEvent({
            target: target,
            searchParams,
            partnerId: context.partner || '',
            source: window.location.href,
            name: item.type.toLowerCase(),
          });
        }
      };

      const Ele = React.createElement(Component, {
        ...item.props,
        handleWidgetClick: handleWidgetClick,
        dynamicFeed: true,
      });

      const handleWidgetImpression = () => {
        console.debug(`widget ${item.type.toLowerCase()} in view`);
        if (trackWidgetEvents) {
          analytics.emitWidgetImpressionEvent({
            searchParams,
            partnerId: context.partner || '',
            source: window.location.href,
            name: item.type.toLowerCase(),
          });
        }
      };

      // Use React.createElement to dynamically render the component with its props
      return (
        <ObservableWidgetContainer
          handleWidgetImpression={handleWidgetImpression}
          threshold={0.2}
          rootMargin={'0px'}
          triggerOnce={true}
        >
          <div
            key={item.instanceId}
            className={`widget-container widget-type-${item.type.toLowerCase()}`}
          >
            {Ele}
          </div>
        </ObservableWidgetContainer>
      );
    });
  }, [
    revision,
    pageData,
    fragments,
    allItems,
    interestsPool,
    isLoadingFeeds,
    routeSchedule,
  ]);

  const emittedPageViewForRevisionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!revision || !context.partner) {
      return;
    }

    const revisionId = revision.id;
    if (emittedPageViewForRevisionRef.current === revisionId) {
      return;
    }
    emittedPageViewForRevisionRef.current = revisionId;

    analytics.emitPageViewEvent({
      numInteractions: getCurrentNumberOfInteractions(),
      partnerId: context.partner || '',
      contentId: '',
      source: window.location.href,
      language,
      title: document.querySelector('title')?.text || '',
      contentOwner: '',
      primaryInterest: '',
      searchParams,
      lbtype: '',
    });
  }, [revision?.id, context.partner, analytics, language, searchParams]);

  const onScroll = useCallback(() => {
    if (!hasScrolled) {
      analytics.emitScrollEvent({
        partnerId: context.partner || '',
        contentId: '',
        source: window.location.href,
        language: i18n.language,
        title: document.querySelector('title')?.text || '',
        contentOwner: '',
        primaryInterest: '',
        searchParams,
      });
      setHasScrolled(true);
    }
  }, [hasScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  // Monitor for API errors and emit pageNotLoadedEvent
  useEffect(() => {
    if (hasApiError && !hasError) {
      const errorQuery =
        feedQueries.find((q) => q.error) ||
        publisherQueries.find((q) => q.error);
      const errorMessage =
        errorQuery?.error instanceof Error
          ? errorQuery.error.message
          : String(errorQuery?.error || 'Unknown API error');

      setHasError(true);

      // If we already emitted page loaded event, we shouldn't have, so we can't undo it
      // But we'll still emit the error event
      analytics.emitPageNotLoadedEvent({
        partnerId: context.partner || '',
        contentId: '',
        source: window.location.href,
        language,
        title: document.querySelector('title')?.text || '',
        contentOwner: '',
        primaryInterest: '',
        searchParams,
        errorType: 'api_error',
        errorMessage,
      });
      analytics.emitErrorEvent({
        errorType: 'api_error',
        errorMessage,
        source: 'TripodLivePreview',
      });
    }
  }, [
    hasApiError,
    feedQueries,
    publisherQueries,
    analytics,
    context.partner,
    language,
    searchParams,
    hasError,
  ]);

  // Emit page loaded event only when page is loaded AND there are no errors
  useEffect(() => {
    // Don't emit if there are any errors
    if (hasError || hasApiError) {
      return;
    }

    // Don't emit if we've already emitted
    if (pageLoadedEventEmitted) {
      return;
    }

    // Don't emit if queries are still loading (wait for them to complete first)
    const queriesStillLoading =
      feedQueries.some((q) => q.isLoading) ||
      publisherQueries.some((q) => q.isLoading);
    if (queriesStillLoading) {
      return;
    }

    // Check if page is loaded
    if (!pageLoadComplete) {
      return;
    }

    // All conditions met: no errors, queries completed, page loaded
    const start = (window as any).pageLoadStart || new Date().getTime();
    const loadTime = new Date().getTime() - start;

    setPageLoadedEventEmitted(true);
    analytics.emitPageLoadedEvent({
      partnerId: context.partner || '',
      contentId: '',
      source: window.location.href,
      language,
      title: document.querySelector('title')?.text || '',
      contentOwner: '',
      primaryInterest: '',
      searchParams,
      loadTime,
    });
  }, [
    hasError,
    hasApiError,
    pageLoadedEventEmitted,
    pageLoadComplete,
    feedQueries,
    publisherQueries,
    analytics,
    context.partner,
    language,
    searchParams,
  ]);

  // Listen for window load event
  useEffect(() => {
    // Only run in browser environment
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    if (document.readyState === 'complete') {
      setPageLoadComplete(true);
      return;
    }

    const handleLoad = () => {
      setPageLoadComplete(true);
    };

    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  if (!revision) {
    return isLoadingTripodData ? (
      <div className="live-preview-loading">
        <ActivityIndicator />
      </div>
    ) : (
      <div>The requested layout revision could not be found.</div>
    );
  }

  return <div className="moment-container">{renderedLayout}</div>;
};

/**
 * Renders a live, server-side rendered preview/page of a Tripod layout revision.
 * Wrapped with error boundary to catch JS errors.
 */
export const TripodLivePreview: React.FC<TripodLivePreviewProps> = (props) => {
  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [searchParams] = useSearchParams();

  const handleError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      analytics.emitPageNotLoadedEvent({
        partnerId: context.partner || '',
        contentId: '',
        source: window.location.href,
        language,
        title: document.querySelector('title')?.text || '',
        contentOwner: '',
        primaryInterest: '',
        searchParams,
        errorType: 'js_error',
        errorMessage: `${error.message} - ${errorInfo.componentStack || ''}`,
      });
      analytics.emitErrorEvent({
        errorType: 'js_error',
        errorMessage: error.message,
        stackTrace: errorInfo.componentStack || error.stack,
        source: 'LivePreviewErrorBoundary',
      });
    },
    [analytics, context.partner, language, searchParams]
  );

  return (
    <LivePreviewErrorBoundary onError={handleError}>
      <TripodLivePreviewInternal {...props} />
    </LivePreviewErrorBoundary>
  );
};
